import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { Moment } from 'moment-timezone';
import * as moment from 'moment-timezone';
import { InternalApiUrls } from 'projects/bis-shared/src/lib/utility/apiurls-internal';
import { ExternalApiUrls } from 'projects/bis-shared/src/lib/utility/apiurls-external';
import { EnvironmentService } from '../environment/environment.service';
import { ISelectOptionGroup } from 'projects/bis-shared/src/lib/dynamic-forms/models/select-option-group';
import { SelectHelpers } from 'projects/bis-shared/src/lib/utility/select-helpers';
import { DocumentFormVersion, DocumentForm, DocumentDownload } from './document.types';
import { SnackbarService } from '../snackbar/snackbar.service';
import { AppUrls } from '../utility/appurls';
import {
  WillHandleErrorMessagingHeader,
  WillHandleErrorMessagingOption,
} from '../utility/http-options';
import { DocumentFormId } from './document-form-id.enum';
import { ObjectTypeCode } from '../utility/system-type-constants';

@Injectable({
  providedIn: 'root',
})

// Typically this service is not used directly.
// It should be called by the internal or external Document services.
export class DocumentSharedService {
  private _validExtension = [
    'pdf',
    'doc',
    'docx',
    'dot',
    'rtf',
    'txt',
    'csv',
    'xls',
    'xlsx',
    'ods',
    'ppt',
    'pptx',
    'msg',
    'xml',
    'tiff',
    'tif',
    'jpeg',
    'jpg',
    'png',
    'avi',
    'flv',
    'mov',
    'mpg',
    'mpeg',
    'mp4',
    'mpe',
    'mpv',
    'wmv',
  ];
  private _validConvertToPdfExtension = [
    'pdf',
    'doc',
    'docx',
    'rtf',
    'txt',
    'csv',
    'xls',
    'xlsx',
    'ods',
    'tiff',
    'tif',
    'jpeg',
    'jpg',
    'png',
  ];
  private signalRDownloads: number[] = [];
  private signalRDownloadsWithAttachments: number[] = [];

  constructor(
    private http: HttpClient,
    private internalApiUrls: InternalApiUrls,
    private externalApiUrls: ExternalApiUrls,
    private environmentService: EnvironmentService,
    private simpleSnackbarService: SnackbarService,
    private router: Router,
  ) {}

  // API calls
  getDocumentDetails<T>(urlFunc: (docId: number) => string, documentId: number): Observable<T> {
    return this.http.get<T>(urlFunc(documentId));
  }

  // TODO  - Should everything pass in a urlFunc even when we know it will be the same?
  getDocumentForms(
    nameFormatter?: (form: DocumentForm) => string,
  ): Observable<ISelectOptionGroup[]> {
    const getDocumentFormsUrl = this.environmentService.isExternalSite
      ? this.externalApiUrls.document.getDocumentForms()
      : this.internalApiUrls.document.getDocumentForms();

    return this.http.get<DocumentForm[]>(getDocumentFormsUrl).pipe(
      map(forms =>
        SelectHelpers.toSelectList(
          forms,
          c => (nameFormatter ? nameFormatter(c) : c.documentFormName),
          c => c.documentFormId,
        ),
      ),
    );
  }

  // TODO  - Should everything pass in a urlFunc even when we know it will be the same?
  getDocumentFormsByCategory(
    categoryId: number,
    nameFormatter?: (form: DocumentForm) => string,
  ): Observable<ISelectOptionGroup[]> {
    const getDocumentFormsByCategoryUrl = this.environmentService.isExternalSite
      ? this.externalApiUrls.document.getDocumentFormsByCategory(categoryId)
      : this.internalApiUrls.document.getDocumentFormsByCategory(categoryId);

    return this.http.get<DocumentForm[]>(getDocumentFormsByCategoryUrl).pipe(
      map(forms =>
        SelectHelpers.toSelectList(
          forms,
          c => (nameFormatter ? nameFormatter(c) : c.documentFormName),
          c => c.documentFormId,
        ),
      ),
    );
  }

  // TODO  - Should everything pass in a urlFunc even when we know it will be the same?
  getDocumentVersions(id: number): Observable<ISelectOptionGroup[]> {
    const getDocumentFormVersionUrl = this.environmentService.isExternalSite
      ? null
      : this.internalApiUrls.document.getDocumentFormVersion(id);

    return this.http.get<DocumentFormVersion[]>(getDocumentFormVersionUrl).pipe(
      tap(forms =>
        forms.forEach(form => (form.effectiveStartDate = moment(form.effectiveStartDate))),
      ),
      map(forms =>
        SelectHelpers.toSelectList(
          forms,
          c => c.documentFormVersion,
          c => c.documentFormVersionId,
        ),
      ),
    );
  }

  downloadRelated = (urlFunc: (docId: number) => string) => (
    documentId: string | number,
    downloadDate?: Moment,
  ): void => {
    if (typeof documentId !== 'number') {
      documentId = parseInt(documentId, 10);
    }

    this.http
      .post<DocumentDownload>(urlFunc(documentId), null, {
        observe: 'response',
        headers: WillHandleErrorMessagingHeader,
      })
      .subscribe(
        res => {
          if (res.body.fileUri) {
            let successMsg = 'Document ready. Click here if it has not downloaded automatically.';
            if (downloadDate && downloadDate !== res.body.lastUpdated) {
              successMsg = 'File has been updated; current version being downloaded.';
            }

            this.simpleSnackbarService.openSnackBarSuccess(successMsg, () =>
              this.router.navigate([AppUrls.recentDownloads()]),
            );
            this.downloadCall(res.body.fileUri, res.body.fileName);
          } else {
            this.signalRDownloadsWithAttachments.push(Number(documentId));
          }
        },
        err => {
          this.simpleSnackbarService.openSnackBarFailure(
            'Documents could not be downloaded at this time.',
          );
          console.error(err);
        },
      );
  };

  downloadRelatedCallback = (urlFunc: (docId: number) => string) => (
    documentId: number | string,
    downloadDate?: Moment,
  ): void => {
    if (typeof documentId !== 'number') {
      documentId = parseInt(documentId, 10);
    }

    const index = this.signalRDownloadsWithAttachments.indexOf(documentId);

    if (index >= 0) {
      this.signalRDownloadsWithAttachments.splice(index, 1);
      this.downloadRelated(urlFunc)(documentId, downloadDate);
    }
  };

  download = (urlFunc: (docId: number) => string) => (
    documentId: number | string,
    downloadDate?: Moment,
  ): void => {
    this.autoDownload(urlFunc)(documentId, false, downloadDate);
  };

  // define as "double function"/function in function
  autoDownload = (urlFunc: (docId: number) => string) => (
    documentId: number | string,
    skipSnackbar: boolean,
    downloadDate?: Moment,
  ): void => {
    if (typeof documentId !== 'number') {
      documentId = parseInt(documentId, 10);
    }

    this.http.post<DocumentDownload>(urlFunc(documentId), null).subscribe(
      res => {
        if (res.fileUri) {
          const downloadCall = () => {
            this.http
              .get(res.fileUri, {
                responseType: 'blob',
                observe: 'response',
              })
              .subscribe((response: HttpResponse<Blob>) => {
                saveAs(response.body, res.fileName.toString());
              });
          };
          if (!skipSnackbar) {
            let successMsg = 'Document ready. Click here if it has not downloaded automatically.';
            if (downloadDate && downloadDate !== res.lastUpdated) {
              successMsg = 'File has been updated; current version being downloaded.';
            }

            this.simpleSnackbarService.openSnackBarSuccess(successMsg, () =>
              this.router.navigate([AppUrls.recentDownloads()]),
            );
          }
          downloadCall();
        } else {
          this.signalRDownloads.push(Number(documentId));
        }
      },
      (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.simpleSnackbarService.openSnackBarFailure(
            'File has been deleted; download no longer available.',
          );
        } else if (err.status === 401) {
          this.simpleSnackbarService.openSnackBarFailure(
            'File has been moved; download no longer available.',
          );
        } else if (err.message) {
          this.simpleSnackbarService.openSnackBarFailure(err.message);
        } else {
          this.simpleSnackbarService.openSnackBarFailure(
            'Document could not be downloaded at this time.',
          );
        }
      },
    );
  };

  downloadCallback = (urlFunc: (docId: number) => string) => (
    documentId: number | string,
    downloadDate?: Moment,
  ): void => {
    if (typeof documentId !== 'number') {
      documentId = parseInt(documentId, 10);
    }

    const index = this.signalRDownloads.indexOf(documentId);

    if (index >= 0) {
      this.signalRDownloads.splice(index, 1);
      this.download(urlFunc)(documentId, downloadDate);
    }
  };

  downloadBulk = (urlFunc: () => string) => (data: any, downloadDate?: Moment): void => {
    this.http.post<[DocumentDownload]>(urlFunc(), data).subscribe(
      docDownloads => {
        for (const res of docDownloads) {
          if (res.fileUri) {
            const downloadCall = () => {
              this.http
                .get(res.fileUri, {
                  responseType: 'blob',
                  observe: 'response',
                })
                .subscribe((response: HttpResponse<Blob>) => {
                  saveAs(response.body, res.fileName.toString());
                });
            };

            let successMsg = 'Document ready. Click here if it has not downloaded automatically.';
            if (downloadDate && downloadDate !== res.lastUpdated) {
              successMsg = 'File has been updated; current version being downloaded.';
            }

            this.simpleSnackbarService.openSnackBarSuccess(successMsg, () =>
              this.router.navigate([AppUrls.recentDownloads()]),
            );
            downloadCall();
          } else {
            this.signalRDownloads.push(res.documentId);
          }
        }
      },
      (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.simpleSnackbarService.openSnackBarFailure(
            'File has been deleted; download no longer available.',
          );
        } else if (err.message) {
          this.simpleSnackbarService.openSnackBarFailure(err.message);
        } else {
          this.simpleSnackbarService.openSnackBarFailure(
            'Document could not be downloaded at this time.',
          );
        }
      },
    );
  };

  autoDownloadCallback = (urlFunc: (docId: number) => string) => (
    documentId: number | string,
    downloadDate?: Moment,
  ): void => {
    if (typeof documentId !== 'number') {
      documentId = parseInt(documentId, 10);
    }

    const index = this.signalRDownloads.indexOf(documentId);

    if (index >= 0) {
      this.signalRDownloads.splice(index, 1);
      this.autoDownload(urlFunc)(documentId, true, downloadDate);
    }
  };

  downloadBulkCallback = (): void => {
    const successMsg =
      'Your downloads are complete! Click here if they were not downloaded automatically.';
    this.simpleSnackbarService.openSnackBarSuccess(successMsg, () =>
      this.router.navigate([AppUrls.recentDownloads()]),
    );
  };

  // Utility Functions
  downloadCall = (fileUri: string, fileName: string) => {
    this.http
      .get(fileUri, {
        responseType: 'blob',
        observe: 'response',
      })
      .subscribe((response: HttpResponse<Blob>) => {
        saveAs(response.body, fileName.toString());
      });
  };

  hasValidFileExtension(fileName: string): boolean {
    const fileExt = fileName.split('.').pop().toLowerCase();

    if (!this._validExtension.includes(fileExt)) {
      return false;
    }
    return true;
  }

  hasValidConvertToPdfFileExtension(fileName: string): boolean {
    const fileExt = fileName.split('.').pop().toLowerCase();

    if (!this._validConvertToPdfExtension.includes(fileExt)) {
      return false;
    }
    return true;
  }

  adjustFilename(filename: string) {
    const FileNameWithReplaceWhiteSpace = this.substituteWhitespaceForFilename(filename);
    const FileNameWithoutSpecialCharacters = FileNameWithReplaceWhiteSpace.replace(
      /[^ 0-9A-Za-z.]+/g,
      '_',
    );
    return this.trimFilename(FileNameWithoutSpecialCharacters);
  }

  substituteWhitespaceForFilename(filename: string) {
    return filename.replace(/\s/g, ' ');
  }

  trimFilename(filename: string) {
    if (filename.length > 200) {
      const fileExt = filename.split('.').pop().toLowerCase();
      const fileExtLength = 199 - fileExt.length;
      return `${filename.slice(0, fileExtLength)}.${fileExt}`;
    } else {
      return filename;
    }
  }

  hasValidFileSize(fileSize: number) {
    if (fileSize >= 30 * 1024 * 1024) {
      return false;
    }
    return true;
  }

  getRelatedDocumentId(relatedObjectId: number, relatedObjectTypeCode: string) {
    return this.http.post<number>(
      this.externalApiUrls.document.getRelatedIdByObjectId(),
      {
        relatedObjectId: relatedObjectId,
        relatedObjectTypeCode: relatedObjectTypeCode,
      },
      WillHandleErrorMessagingOption,
    );
  }

  shouldDisplayRelatedEntity(documentFormId: number, relatedObjectTypeCode: string) {
    const documentForms = [DocumentFormId.MedicalBillingDispute];

    return (
      documentForms.some(id => id === documentFormId) ||
      relatedObjectTypeCode === ObjectTypeCode.MedicalBillingDispute
    );
  }
}
